/**
 * IndexedDB-backed storage for the browser-offline sell resilience feature
 * (docs/pospak-business-fit-audit.md §4 gap — Dukaan-POS's `runOrQueue` +
 * IndexedDB pattern). Four object stores, one database:
 *
 *   - `sell-queue`     — queued "add a line" ops for a bill that already has
 *                        a server id (offline-queue.ts's original scope).
 *   - `catalog-cache`  — one record per shop: every simple sellable variant
 *                        with its resolved price, plus the business/shop
 *                        header fields a receipt needs, refreshed while
 *                        online (offline-catalog.ts).
 *   - `id-blocks`      — one record per shop: the currently-held range of
 *                        pre-reserved bill numbers (offline-block.ts).
 *   - `offline-bills`  — bills started (and possibly paid) with zero
 *                        connectivity from the very first line, replayed to
 *                        the server on reconnect (offline-bill.ts).
 *
 * Deliberately plain `indexedDB`, no `idb` dependency — a handful of
 * key-value operations per store, not worth a wrapper library.
 */
const DB_NAME = 'pospak-offline';
const DB_VERSION = 2;

export const STORE_SELL_QUEUE = 'sell-queue';
export const STORE_CATALOG_CACHE = 'catalog-cache';
export const STORE_ID_BLOCKS = 'id-blocks';
export const STORE_OFFLINE_BILLS = 'offline-bills';

export interface QueuedLineOp {
  /** Idempotency key — also the IndexedDB primary key. Sent to the server
   * as `clientOpId` so a replay can never add the same line twice
   * (Bill/BillLine's `clientOpId` unique constraint, prisma/schema.prisma). */
  clientOpId: string;
  billId: string;
  shopId: string;
  /** The exact body this op will POST to `/bills/:billId/lines` once replayed. */
  body: { variantId: string; qtyMilli: number; unitPricePaisa?: number; clientOpId: string };
  /** For the optimistic row shown while pending — never sent to the server. */
  preview: { productName: string; variantName: string; unit: string; pricePaisa: number };
  createdAt: number;
  status: 'PENDING' | 'FAILED';
  lastError?: string;
}

export interface OfflineCatalogItem {
  variantId: string;
  productId: string;
  productName: string;
  variantName: string;
  unit: string;
  sku: string | null;
  barcodes: string[];
  isService: boolean;
  trackStock: boolean;
  taxRateBps: number;
  taxExempt: boolean;
  pricePaisa: number;
  priceMode: string;
  availableMilli: number | null;
}

export interface CatalogCacheRecord {
  shopId: string;
  items: OfflineCatalogItem[];
  shop: { code: string; name: string; phone: string | null; address: string | null };
  business: { name: string; phone: string | null; address: string | null; ntn: string | null; strn: string | null };
  fetchedAt: number;
}

export interface IdBlockRecord {
  shopId: string;
  blockId: string;
  prefix: string;
  shopCode: string;
  fromValue: number;
  toValue: number;
  /** Next value this terminal will hand out — starts at `fromValue`. */
  nextValue: number;
}

export interface OfflineBillLine {
  /** Client-local id — stable React key and qty-update target; never sent to the server. */
  lineId: string;
  variantId: string;
  productName: string;
  variantName: string;
  unit: string;
  qtyMilli: number;
  unitPricePaisa: number;
  priceMode: string;
  taxRateBps: number;
  taxExempt: boolean;
}

export interface OfflineBillPayment {
  method: string;
  amountPaisa: number;
  reference?: string;
  tenderedPaisa?: number;
}

export interface OfflineBillRecord {
  /** Also `Bill.clientOpId` once replayed — the one idempotency key that
   * follows this sale from its very first line to its server row. */
  localId: string;
  shopId: string;
  billNo: string;
  status: 'OPEN' | 'PAID' | 'FAILED';
  lastError?: string;
  /** Set once the create step of a replay lands — the real server bill,
   * even if the pay step after it then fails (a price/stock conflict). The
   * conflict-review screen links here instead of building its own
   * resolution UI: the bill already exists, in DRAFT/UNPAID, exactly where
   * every other unfinished bill lives. */
  serverBillId?: string;
  createdAt: number;
  paidAt?: number;
  customerName: string | null;
  customerPhone: string | null;
  lines: OfflineBillLine[];
  payments: OfflineBillPayment[];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SELL_QUEUE)) {
        db.createObjectStore(STORE_SELL_QUEUE, { keyPath: 'clientOpId' });
      }
      if (!db.objectStoreNames.contains(STORE_CATALOG_CACHE)) {
        db.createObjectStore(STORE_CATALOG_CACHE, { keyPath: 'shopId' });
      }
      if (!db.objectStoreNames.contains(STORE_ID_BLOCKS)) {
        db.createObjectStore(STORE_ID_BLOCKS, { keyPath: 'shopId' });
      }
      if (!db.objectStoreNames.contains(STORE_OFFLINE_BILLS)) {
        db.createObjectStore(STORE_OFFLINE_BILLS, { keyPath: 'localId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(store: string, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const s = tx.objectStore(store);
    const req = fn(s);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* -------------------------------------------------------------------------- */
/* sell-queue                                                                  */
/* -------------------------------------------------------------------------- */

export async function enqueueOp(op: QueuedLineOp): Promise<void> {
  await withStore(STORE_SELL_QUEUE, 'readwrite', (s) => s.put(op));
}

export async function listQueuedOps(): Promise<QueuedLineOp[]> {
  const all = await withStore<QueuedLineOp[]>(STORE_SELL_QUEUE, 'readonly', (s) => s.getAll());
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeOp(clientOpId: string): Promise<void> {
  await withStore(STORE_SELL_QUEUE, 'readwrite', (s) => s.delete(clientOpId));
}

export async function markOpFailed(clientOpId: string, error: string): Promise<void> {
  const op = await withStore<QueuedLineOp | undefined>(STORE_SELL_QUEUE, 'readonly', (s) => s.get(clientOpId));
  if (!op) return;
  await enqueueOp({ ...op, status: 'FAILED', lastError: error });
}

/* -------------------------------------------------------------------------- */
/* catalog-cache                                                              */
/* -------------------------------------------------------------------------- */

export async function putCatalogCache(record: CatalogCacheRecord): Promise<void> {
  await withStore(STORE_CATALOG_CACHE, 'readwrite', (s) => s.put(record));
}

export async function getCatalogCache(shopId: string): Promise<CatalogCacheRecord | undefined> {
  return withStore(STORE_CATALOG_CACHE, 'readonly', (s) => s.get(shopId));
}

/* -------------------------------------------------------------------------- */
/* id-blocks                                                                  */
/* -------------------------------------------------------------------------- */

export async function putIdBlock(record: IdBlockRecord): Promise<void> {
  await withStore(STORE_ID_BLOCKS, 'readwrite', (s) => s.put(record));
}

export async function getIdBlock(shopId: string): Promise<IdBlockRecord | undefined> {
  return withStore(STORE_ID_BLOCKS, 'readonly', (s) => s.get(shopId));
}

/* -------------------------------------------------------------------------- */
/* offline-bills                                                             */
/* -------------------------------------------------------------------------- */

export async function putOfflineBill(record: OfflineBillRecord): Promise<void> {
  await withStore(STORE_OFFLINE_BILLS, 'readwrite', (s) => s.put(record));
}

export async function getOfflineBillRecord(localId: string): Promise<OfflineBillRecord | undefined> {
  return withStore(STORE_OFFLINE_BILLS, 'readonly', (s) => s.get(localId));
}

export async function listOfflineBillRecords(): Promise<OfflineBillRecord[]> {
  const all = await withStore<OfflineBillRecord[]>(STORE_OFFLINE_BILLS, 'readonly', (s) => s.getAll());
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeOfflineBill(localId: string): Promise<void> {
  await withStore(STORE_OFFLINE_BILLS, 'readwrite', (s) => s.delete(localId));
}
